import { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Modal, Button } from "@etframework/ui.components";
import { useCreateStage } from "../../data/mutations/stageMutations";
import { useTranslation } from "react-i18next";
import useModalEvent from "../../hooks/useModalEvent";
import { toast } from "react-toastify";
import { StageNameTextBox } from "../../components/StageComponents/StageModalComponents/StageNameTextbox";
import { StageColorPicker } from "../../components/StageComponents/StageModalComponents/StageColorPicker";
import { useParams } from "react-router-dom";
import { ValidationProvider, useValidation } from "@etframework/ui.components";

export const CreateStageModal = () => {
  return (
    <ValidationProvider>
      <InnerCreateStageModal />
    </ValidationProvider>
  );
};
export const InnerCreateStageModal = () => {
  const stageNameRef = useRef(null);
  const stageColorRef = useRef(null);

  const [newStage, setNewStage] = useState({});

  const { mutate: createStage, isPending } = useCreateStage();
  const { setValidationErrors, clearValidationErrors } = useValidation();
  const { boardId } = useParams();
  const { t } = useTranslation(["Stages"], {
    useSuspense: false,
  });

  //Modal
  const modalRef = useRef();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const [showModal, setShowModal] = useState(false);

  const onModelBeforeClose = () => {};

  const closeModal = () => {
    setShowModal(false);
    clearValidationErrors();
    if (newStage) newStage?.ref?.current?.setIsAdding(false);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const toggleModal = (event) => {
    modalRef.current.open();
    setNewStage(event.detail);
    if (showModal) {
      closeModal();
      return;
    } else {
      openModal();
    }
  };

  const checkRequiredData = () => {
    const nameValue = stageNameRef.current.getValue();
    const isNameValid = nameValue != "";
    const isValid = isNameValid;

    if (!isNameValid) stageNameRef.current.setError(t("Required"));

    stageNameRef.current.setShowError(!isNameValid);

    return isValid;
  };

  const onCreateStage = () => {
    if (checkRequiredData()) {
      const nameValue = stageNameRef.current.getValue();
      const colorValue = stageColorRef.current.getValue();

      const postData = {
        BoardId: boardId,
        Title: nameValue,
        Color: colorValue,
        Order: newStage?.position,
      };

      createStage(
        { stage: postData },
        {
          onSuccess: () => {
            modalRef.current.close();
            toast.success(t("Success"), { position: "bottom-left" });
          },
          onError: (errors) => {
            setValidationErrors(errors?.data);
            toast.error(t("ErrorMsg"), { position: "bottom-left" });
          },
        }
      );
    }
  };

  const ModalContent = (
    <div className="et-stage-modal">
      <div>
        <span className="stage-header d-block">{t("NewStage")}</span>
      </div>

      <div className="row">
        <div className="col">
          <StageNameTextBox
            id={ModalEvents.CreateStageModal.Id + "_name-input"}
            ref={stageNameRef}
            name="Title"
            nameLabel={t("StageNameLabel")}
            stageName={newStage?.title}
            change={(e) => {
              setNewStage((prev) => ({ ...prev, title: e.value }));
              clearValidationErrors();
            }}
          />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <StageColorPicker
            id={ModalEvents.CreateStageModal.Id + "_color-picker-input"}
            ref={stageColorRef}
            colorLabel={t("StageColorLabel")}
            color={newStage?.color}
            change={(e) => {
              setNewStage((prev) => ({ ...prev, color: e.value }));
            }}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end stage-modal-btns mx-auto gap-3">
        <Button
          text={t("Cancel")}
          className="btn-tertiary"
          onClick={() => modalRef.current.close()}
        />
        <Button
          id={ModalEvents.CreateStageModal.Id + "_add-stage-button"}
          text={t("AddStage")}
          className="btn btn-primary"
          onClick={onCreateStage}
          loading={isPending}
        />
      </div>
    </div>
  );

  useRegisterEvent(ModalEvents.CreateStageModal, toggleModal);

  return ReactDOM.createPortal(
    <Modal
      id={ModalEvents.CreateStageModal.Id}
      ref={modalRef}
      isModal
      showCloseIcon={true}
      visible={showModal}
      content={ModalContent}
      close={closeModal}
      open={openModal}
      beforeClose={onModelBeforeClose}
      minHeight="20vh"
      width="400px"
    ></Modal>,
    document.body
  );
};
